import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  Chip,
} from "@mui/material";
import {
  DatePicker,
  TimePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CheckIcon from "@mui/icons-material/Check";

// Hooks
import useCourses from "../../../../hooks/useCourses";
import useCoursePlanningLogger from "../../../../hooks/useCoursePlanningLogger";
import useSchoolDropdown from "../../../SchoolUsers/hooks/useSchoolDropdown";
import useTermData from "../../CcpAdminTerm/hooks/useTermData";

// Context and Auth
import { SchoolContext } from "../../../../config/SchoolContext";
import { useAuth } from "../../../../config/AuthContext";

// Firebase
import { db } from "../../../../config/firebase";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
  orderBy,
} from "firebase/firestore";

// Components
import SearchableCourseSelect from "../../../common/SearchableCourseSelect";
import { formatDate } from "../../../../utils/formatDate";
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ConfirmDeleteDialog from "../../../common/ConfirmDeleteDialog";

const AdminCoursePlanningForm = ({
  onSubmit,
  onCancel,
  schoolId: propSchoolId,
}) => {
  const { schoolId: contextSchoolId } = useContext(SchoolContext);

  // Use propSchoolId if provided, otherwise fall back to contextSchoolId
  const schoolId = propSchoolId || contextSchoolId;

  // Debug logging
  console.log("AdminCoursePlanningForm - propSchoolId:", propSchoolId);
  console.log("AdminCoursePlanningForm - contextSchoolId:", contextSchoolId);
  console.log("AdminCoursePlanningForm - resolved schoolId:", schoolId);

  const { currentUser } = useAuth();
  const {
    courses,
    loading: coursesLoading,
    error: coursesError,
  } = useCourses();

  // Get schools data to lookup school name
  const { data: schools, loading: schoolsLoading } = useSchoolDropdown();

  // Get terms data from CcpTerm collection
  const { data: ccpTerms, loading: termsLoading } = useTermData();

  // Use the course planning logger hook
  const { logAddition, logBulkUpdate, logDeletion, logEvent } =
    useCoursePlanningLogger();

  // Initial row structure (now includes change request fields for admin)
  const createEmptyRow = () => ({
    id: Date.now() + Math.random(),
    courseName: "",
    numberOfSections: "",
    startDate: null,
    endDate: null,
    instructorType: "",
    // HS Faculty fields
    hsInstructorName: "",
    hsComments: "",
    // Courses with Sinclair fields
    proctorName: "",
    term: "",
    modality: "",
    days: "",
    startTime: "",
    endTime: "",
    // Change request fields (for admin to see and manage)
    changeAction: "",
    specificChange: "",
    changeRequestStatus: "", // approved, rejected, or empty
    originalChangeAction: "",
    originalSpecificChange: "",
    approvedBy: "",
    approvedAt: "",
    rejectedBy: "",
    rejectedAt: "",
    // Track if this row has been saved
    isSaved: false,
    // Track original values for saved rows to detect changes
    originalValues: null,
    // Track if this row has pending changes
    hasChanges: false,
    // Track if this row has a pending change request
    isPending: false,
    // Fields for handling change requests
    changeRequestId: null,
    fieldChanges: null,
    requestedValues: null,
  });

  const [rows, setRows] = useState([createEmptyRow()]);
  const [loading, setLoading] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    rowToDelete: null,
  });
  const [editingRows, setEditingRows] = useState(new Set());
  const [showEditMessage, setShowEditMessage] = useState(false);

  // Add state for cancel confirmation dialog
  const [cancelDialog, setCancelDialog] = useState({
    open: false,
    row: null,
  });

  // Use dynamic term options from CcpTerm collection
  const termOptions = ccpTerms?.map((term) => term.TermName) || [
    "FA A Term",
    "FA Full Term",
    "FA 12 Wk Term",
    "FA B Term",
    "SP A Term",
    "SP Full Term",
    "SP 12 Wk",
    "SP B Term",
  ];

  // Debug logging for term options
  console.log("AdminCoursePlanningForm - ccpTerms:", ccpTerms);
  console.log("AdminCoursePlanningForm - termOptions:", termOptions);
  console.log("AdminCoursePlanningForm - termsLoading:", termsLoading);

  const modalityOptions = ["Face 2 Face", "Online"];

  const instructorTypeOptions = ["HS Faculty", "Courses W Sinclair"];

  // Helper function to get selected term data from CcpTerm collection
  const getSelectedTermData = (termName) => {
    return ccpTerms?.find((term) => term.TermName === termName);
  };

  // Helper function to determine field visibility based on instructor type and modality
  const getFieldVisibility = (instructorType, modality) => {
    const baseFields = {
      courseName: true,
      numberOfSections: true,
      instructorType: true,
    };

    if (instructorType === "HS Faculty") {
      return {
        ...baseFields,
        term: true,
        startDate: true,
        endDate: true,
        modality: false,
        days: false,
        startTime: false,
        endTime: false,
        proctorName: false,
        hsInstructorName: true,
        hsComments: true,
      };
    } else if (instructorType === "Courses W Sinclair") {
      const baseFieldsWithSinclair = {
        ...baseFields,
        term: true,
        startDate: true,
        endDate: true,
        modality: true,
        hsInstructorName: false,
        hsComments: true,
      };

      if (modality === "Face 2 Face") {
        return {
          ...baseFieldsWithSinclair,
          days: true,
          startTime: true,
          endTime: true,
          proctorName: true,
        };
      } else if (modality === "Online") {
        return {
          ...baseFieldsWithSinclair,
          days: false,
          startTime: false,
          endTime: false,
          proctorName: true,
        };
      } else {
        // No modality selected yet
        return {
          ...baseFieldsWithSinclair,
          days: false,
          startTime: false,
          endTime: false,
          proctorName: false,
        };
      }
    }

    // Default - show only base fields
    return {
      ...baseFields,
      term: false,
      startDate: false,
      endDate: false,
      modality: false,
      days: false,
      startTime: false,
      endTime: false,
      proctorName: false,
      hsInstructorName: false,
      hsComments: false,
    };
  };

  // Check if row is empty
  const isRowEmpty = (row) => {
    return (
      !row.courseName &&
      !row.numberOfSections &&
      !row.startDate &&
      !row.endDate &&
      !row.instructorType &&
      !row.hsInstructorName &&
      !row.hsComments &&
      !row.proctorName &&
      !row.term &&
      !row.modality &&
      !row.days &&
      !row.startTime &&
      !row.endTime
    );
  };

  const handleRowChange = (rowId, field, value) => {
    // Mark this row as being edited
    setEditingRows((prev) => new Set(prev).add(rowId));
    setShowEditMessage(true);

    setRows((prev) =>
      prev.map((row) => {
        if (row.id === rowId) {
          const updatedRow = { ...row, [field]: value };

          // Special handling for instructor type change
          if (field === "instructorType") {
            // Clear fields that become irrelevant when instructor type changes
            updatedRow.term = "";
            updatedRow.startDate = null;
            updatedRow.endDate = null;
            updatedRow.modality = "";
            updatedRow.days = "";
            updatedRow.startTime = "";
            updatedRow.endTime = "";
            updatedRow.proctorName = "";
            updatedRow.hsComments = "";
          }

          // Special handling for term change - auto-populate dates from CcpTerm collection
          if (
            field === "term" &&
            (updatedRow.instructorType === "Courses W Sinclair" ||
              updatedRow.instructorType === "HS Faculty")
          ) {
            if (value) {
              // Term selected - auto-populate dates
              const termData = getSelectedTermData(value);
              if (termData && termData.StartDate && termData.EndDate) {
                try {
                  updatedRow.startDate = dayjs(termData.StartDate);
                  updatedRow.endDate = dayjs(termData.EndDate);
                } catch (error) {
                  console.error("Error parsing term dates:", error);
                  updatedRow.startDate = null;
                  updatedRow.endDate = null;
                }
              } else {
                // Clear dates if term data is incomplete
                updatedRow.startDate = null;
                updatedRow.endDate = null;
              }
            } else {
              // Term cleared - clear dates as well
              updatedRow.startDate = null;
              updatedRow.endDate = null;
            }
          }

          // Special handling for modality change
          if (field === "modality") {
            // Clear fields that become irrelevant when modality changes
            updatedRow.days = "";
            updatedRow.startTime = "";
            updatedRow.endTime = "";
            updatedRow.proctorName = "";
          }

          // For saved rows, track if there are changes from original values
          if (row.isSaved && row.originalValues) {
            // Helper function to compare values (handles dayjs objects)
            const valuesAreEqual = (val1, val2) => {
              // Handle dayjs objects
              if (val1 && val1.$isDayjsObject && val2 && val2.$isDayjsObject) {
                return val1.isSame(val2);
              }
              // Handle case where one is dayjs and other is null/undefined
              if (
                (val1 && val1.$isDayjsObject) ||
                (val2 && val2.$isDayjsObject)
              ) {
                if (!val1 && !val2) return true;
                if (!val1 || !val2) return false;
                return val1.isSame && val1.isSame(val2);
              }
              // Handle regular values
              return val1 === val2;
            };

            // Check if any field has changed from original
            const hasAnyChanges = Object.keys(row.originalValues).some(
              (key) => {
                const currentValue = key === field ? value : updatedRow[key];
                const originalValue = row.originalValues[key];
                return !valuesAreEqual(originalValue, currentValue);
              },
            );

            updatedRow.hasChanges = hasAnyChanges;
          }

          return updatedRow;
        }
        return row;
      }),
    );
  };

  // Load existing course planning records for this school
  const loadExistingRecords = async () => {
    if (!schoolId) {
      console.log("AdminCoursePlanningForm - No schoolId, skipping load");
      return;
    }

    try {
      setLoadingExisting(true);
      console.log(
        "AdminCoursePlanningForm - Loading records for schoolId:",
        schoolId,
      );

      const q = query(
        collection(db, "CoursePlanningForm"),
        where("schoolId", "==", schoolId),
        orderBy("createdAt", "desc"),
      );

      const querySnapshot = await getDocs(q);
      const existingRecords = [];

      console.log(
        "AdminCoursePlanningForm - Found",
        querySnapshot.size,
        "records",
      );

      // Load change requests for this school
      let changeRequestsMap = {};
      try {
        console.log(
          "AdminCoursePlanningForm - Loading change requests for school:",
          schoolId,
        );
        const changeRequestsQuery = query(
          collection(db, "CoursePlanningChangeRequests"),
          where("schoolId", "==", schoolId),
          where("status", "==", "pending"),
        );
        const changeRequestsSnapshot = await getDocs(changeRequestsQuery);

        console.log(
          "AdminCoursePlanningForm - Found",
          changeRequestsSnapshot.size,
          "pending change requests",
        );

        changeRequestsSnapshot.forEach((doc) => {
          const changeRequest = doc.data();
          console.log(
            "AdminCoursePlanningForm - Change request:",
            doc.id,
            changeRequest,
          );
          changeRequestsMap[changeRequest.originalCourseId] = {
            id: doc.id,
            ...changeRequest,
          };
        });
      } catch (changeRequestError) {
        console.error("Error loading change requests:", changeRequestError);
      }

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log("AdminCoursePlanningForm - Loading record:", doc.id, data);

        const recordData = {
          id: doc.id, // Use Firestore document ID
          courseName: data.courseName || "",
          numberOfSections: data.numberOfSections || "",
          startDate: data.startDate ? dayjs(data.startDate) : null,
          endDate: data.endDate ? dayjs(data.endDate) : null,
          instructorType: data.instructorType || "",
          hsInstructorName: data.hsInstructorName || "",
          hsComments: data.hsComments || "",
          proctorName: data.proctorName || "",
          term: data.term || "",
          modality: data.modality || "",
          days: data.days || "",
          startTime: data.startTime || "",
          endTime: data.endTime || "",
          changeAction: data.changeAction || "",
          specificChange: data.specificChange || "",
          status: data.status || "", // Load cancellation status
          changeRequestStatus: data.changeRequestStatus || "", // approved, rejected, or empty
          originalChangeAction: data.originalChangeAction || "",
          originalSpecificChange: data.originalSpecificChange || "",
          approvedBy: data.approvedBy || "",
          approvedAt: data.approvedAt || "",
          rejectedBy: data.rejectedBy || "",
          rejectedAt: data.rejectedAt || "",
          isSaved: true, // Mark existing records as saved
          hasChanges: false, // No changes initially
          isPending: false, // Will be set below if there's a pending change request
        };

        // Check if there's a pending change request for this course
        const changeRequest = changeRequestsMap[doc.id];
        if (changeRequest) {
          console.log(
            "AdminCoursePlanningForm - Found change request for course:",
            doc.id,
            changeRequest,
          );

          recordData.isPending = true;
          recordData.changeAction =
            changeRequest.requestType || "Update Course";
          recordData.specificChange =
            changeRequest.reason || "Course details updated";
          recordData.changeRequestId = changeRequest.id;
          recordData.fieldChanges = changeRequest.fieldChanges || {};

          // Store the requested (new) values to show what the admin needs to approve
          recordData.requestedValues = {};
          if (changeRequest.fieldChanges) {
            Object.keys(changeRequest.fieldChanges).forEach((fieldName) => {
              const change = changeRequest.fieldChanges[fieldName];
              if (change && change.newValue !== undefined) {
                recordData.requestedValues[fieldName] = change.newValue;
              }
            });
          }

          console.log(
            "AdminCoursePlanningForm - Requested values:",
            recordData.requestedValues,
          );
        }

        // Store original values for change tracking
        recordData.originalValues = {
          courseName: recordData.courseName,
          numberOfSections: recordData.numberOfSections,
          startDate: recordData.startDate,
          endDate: recordData.endDate,
          instructorType: recordData.instructorType,
          hsInstructorName: recordData.hsInstructorName,
          hsComments: recordData.hsComments,
          proctorName: recordData.proctorName,
          term: recordData.term,
          modality: recordData.modality,
          days: recordData.days,
          startTime: recordData.startTime,
          endTime: recordData.endTime,
          changeAction: recordData.changeAction,
          specificChange: recordData.specificChange,
        };

        // Debug logging for term value
        console.log("AdminCoursePlanningForm - Record term value:", data.term);
        console.log(
          "AdminCoursePlanningForm - RecordData term value:",
          recordData.term,
        );

        existingRecords.push(recordData);
      });

      // Set rows with existing records plus one empty row for new entries
      if (existingRecords.length > 0) {
        setRows([...existingRecords, createEmptyRow()]);
      } else {
        setRows([createEmptyRow()]);
      }

      console.log(
        `Loaded ${existingRecords.length} existing course planning records for school ${schoolId}`,
      );
      console.log(
        "Records with pending changes:",
        existingRecords.filter((r) => r.isPending).length,
      );
    } catch (error) {
      console.error("Error loading existing records:", error);
      setSnackbar({
        open: true,
        message: "Error loading existing records",
        severity: "error",
      });
    } finally {
      setLoadingExisting(false);
    }
  };

  // Load existing records when component mounts or schoolId changes
  // Wait for terms to load before loading records
  useEffect(() => {
    if (!termsLoading && schoolId) {
      loadExistingRecords();
    }
  }, [schoolId, termsLoading]);

  // Debug logging for rows and term options
  useEffect(() => {
    console.log(
      "AdminCoursePlanningForm - Rows updated:",
      rows.map((r) => ({ id: r.id, term: r.term, isSaved: r.isSaved })),
    );
    console.log(
      "AdminCoursePlanningForm - Available term options:",
      termOptions,
    );
    rows.forEach((row) => {
      if (row.term && !termOptions.includes(row.term)) {
        console.warn(
          `AdminCoursePlanningForm - Term "${row.term}" for row ${row.id} is not in available options:`,
          termOptions,
        );
      }
    });
  }, [rows, termOptions]);

  const addRow = () => {
    setRows((prev) => [...prev, createEmptyRow()]);
  };

  const deleteRow = (rowId) => {
    const rowToDelete = rows.find((row) => row.id === rowId);

    if (rowToDelete?.isSaved) {
      // For saved rows, show confirmation dialog before deleting
      setDeleteDialog({
        open: true,
        rowToDelete: rowToDelete,
      });
    } else {
      // For unsaved rows, just remove from state (no logging needed)
      if (rows.length > 1) {
        setRows((prev) => prev.filter((row) => row.id !== rowId));
      }
    }
  };

  const handleDeleteConfirm = async () => {
    const { rowToDelete } = deleteDialog;

    if (rowToDelete) {
      try {
        // Log the deletion
        await logDeletion(rowToDelete);

        // Delete from Firestore if it's a saved row with a valid document ID
        if (
          rowToDelete.isSaved &&
          rowToDelete.id &&
          typeof rowToDelete.id === "string"
        ) {
          const docRef = doc(db, "CoursePlanningForm", rowToDelete.id);
          await deleteDoc(docRef);
        }

        // Remove from local state
        setRows((prev) => prev.filter((row) => row.id !== rowToDelete.id));
        setSnackbar({
          open: true,
          message: "Course planning entry deleted successfully",
          severity: "success",
        });
      } catch (error) {
        console.error("Error deleting course planning entry:", error);
        setSnackbar({
          open: true,
          message: "Error deleting entry. Please try again.",
          severity: "error",
        });
      }
    }

    // Close the dialog
    setDeleteDialog({
      open: false,
      rowToDelete: null,
    });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({
      open: false,
      rowToDelete: null,
    });
  };

  const validateRow = (row) => {
    const errors = [];
    const fieldVisibility = getFieldVisibility(
      row.instructorType,
      row.modality,
    );

    if (!row.courseName) errors.push("Course Name is required");
    if (
      !row.numberOfSections ||
      isNaN(row.numberOfSections) ||
      row.numberOfSections <= 0
    ) {
      errors.push("Number of Sections must be a positive number");
    }

    // For "Courses W Sinclair", start/end dates are required
    // For "HS Faculty", start/end dates are optional (shown but not required)
    if (row.instructorType === "Courses W Sinclair") {
      if (fieldVisibility.startDate && !row.startDate) {
        errors.push("Start Date is required");
      }
      if (fieldVisibility.endDate && !row.endDate) {
        errors.push("End Date is required");
      }
    }

    return errors;
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      // Identify rows with changes (for updates) and new rows (for additions)
      const changedRows = rows.filter((row) => row.isSaved && row.hasChanges);
      const unsavedRows = rows.filter((row) => !row.isSaved);
      const rowsToValidate = unsavedRows.filter((row) => !isRowEmpty(row));

      // Validate only unsaved rows that have some data entered
      const allErrors = [];

      rowsToValidate.forEach((row, index) => {
        const errors = validateRow(row);
        if (errors.length > 0) {
          const actualRowIndex = rows.indexOf(row) + 1; // Get actual position in all rows
          allErrors.push(`Row ${actualRowIndex}: ${errors.join(", ")}`);
        }
      });

      if (allErrors.length > 0) {
        setSnackbar({
          open: true,
          message: `Validation errors: ${allErrors.join("; ")}`,
          severity: "error",
        });
        return;
      }

      // Check if there are any changes to save
      if (rowsToValidate.length === 0 && changedRows.length === 0) {
        setSnackbar({
          open: true,
          message: "No changes to save",
          severity: "info",
        });
        return;
      }

      // Prepare form data only for non-empty unsaved rows
      const formDataRows = rowsToValidate.map((row) => ({
        courseName: row.courseName,
        numberOfSections: parseInt(row.numberOfSections),
        startDate: row.startDate ? row.startDate.toISOString() : null,
        endDate: row.endDate ? row.endDate.toISOString() : null,
        instructorType: row.instructorType,
        hsInstructorName: row.hsInstructorName,
        hsComments: row.hsComments,
        proctorName: row.proctorName,
        term: row.term,
        modality: row.modality,
        days: row.days,
        startTime: row.startTime,
        endTime: row.endTime,
        changeAction: row.changeAction,
        specificChange: row.specificChange,
        isPending: row.isPending,
        schoolId: schoolId,
        createdBy: currentUser?.uid,
        createdAt: new Date().toISOString(),
      }));

      // Call onSubmit if provided, otherwise save directly
      let newDocIds = [];
      if (onSubmit) {
        await onSubmit(formDataRows);
      } else {
        // Save new rows as separate documents and capture their IDs
        const savePromises = formDataRows.map(async (formData) => {
          return addDoc(collection(db, "CoursePlanningForm"), formData);
        });

        const savedDocs = await Promise.all(savePromises);
        newDocIds = savedDocs.map((docRef) => docRef.id);

        // Update existing changed rows in Firestore
        const updatePromises = changedRows.map(async (row) => {
          if (row.id && typeof row.id === "string") {
            // Use the document ID to update the existing document
            const docRef = doc(db, "CoursePlanningForm", row.id);
            const updateData = {
              courseName: row.courseName,
              numberOfSections: parseInt(row.numberOfSections),
              startDate: row.startDate ? row.startDate.toISOString() : null,
              endDate: row.endDate ? row.endDate.toISOString() : null,
              instructorType: row.instructorType,
              hsInstructorName: row.hsInstructorName,
              hsComments: row.hsComments,
              proctorName: row.proctorName,
              term: row.term,
              modality: row.modality,
              days: row.days,
              startTime: row.startTime,
              endTime: row.endTime,
              changeAction: row.changeAction,
              specificChange: row.specificChange,
              isPending: row.isPending,
              updatedAt: new Date().toISOString(),
              updatedBy: currentUser?.uid,
            };
            return updateDoc(docRef, updateData);
          }
        });

        await Promise.all(updatePromises);
      }

      // Log each new entry
      for (const row of rowsToValidate) {
        await logAddition(row);
      }

      // Log updates for changed rows
      for (const row of changedRows) {
        if (row.originalValues) {
          // Helper function to compare values (handles dayjs objects)
          const valuesAreEqual = (val1, val2) => {
            // Handle dayjs objects
            if (val1 && val1.$isDayjsObject && val2 && val2.$isDayjsObject) {
              return val1.isSame(val2);
            }
            // Handle case where one is dayjs and other is null/undefined
            if (
              (val1 && val1.$isDayjsObject) ||
              (val2 && val2.$isDayjsObject)
            ) {
              if (!val1 && !val2) return true;
              if (!val1 || !val2) return false;
              return val1.isSame && val1.isSame(val2);
            }
            // Handle regular values
            return val1 === val2;
          };

          // Log a summary of all changes for this row
          const changedFields = [];
          Object.keys(row.originalValues).forEach((field) => {
            if (!valuesAreEqual(row.originalValues[field], row[field])) {
              changedFields.push({
                field,
                oldValue: row.originalValues[field],
                newValue: row[field],
              });
            }
          });

          if (changedFields.length > 0) {
            await logBulkUpdate(changedFields, row, row.originalValues);
          }
        }
      }

      // Mark all current rows as saved and update original values
      setRows((prev) => {
        const updatedRows = prev.map((row) => {
          if (row.isSaved && row.hasChanges) {
            // Update original values for changed rows
            const updatedRow = { ...row, hasChanges: false };
            updatedRow.originalValues = {
              courseName: row.courseName,
              numberOfSections: row.numberOfSections,
              startDate: row.startDate,
              endDate: row.endDate,
              instructorType: row.instructorType,
              hsInstructorName: row.hsInstructorName,
              hsComments: row.hsComments,
              proctorName: row.proctorName,
              term: row.term,
              modality: row.modality,
              days: row.days,
              startTime: row.startTime,
              endTime: row.endTime,
            };
            return updatedRow;
          } else if (!row.isSaved && !isRowEmpty(row)) {
            // Mark new rows as saved and assign the Firestore document ID
            const rowIndex = rowsToValidate.indexOf(row);
            const firestoreId =
              rowIndex >= 0 && rowIndex < newDocIds.length
                ? newDocIds[rowIndex]
                : row.id;

            const savedRow = {
              ...row,
              id: firestoreId, // Use the actual Firestore document ID
              isSaved: true,
              hasChanges: false,
            };
            savedRow.originalValues = {
              courseName: row.courseName,
              numberOfSections: row.numberOfSections,
              startDate: row.startDate,
              endDate: row.endDate,
              instructorType: row.instructorType,
              hsInstructorName: row.hsInstructorName,
              hsComments: row.hsComments,
              proctorName: row.proctorName,
              term: row.term,
              modality: row.modality,
              days: row.days,
              startTime: row.startTime,
              endTime: row.endTime,
              changeAction: row.changeAction,
              specificChange: row.specificChange,
            };
            return savedRow;
          }
          return row;
        });

        // Only add a new empty row if there isn't already an empty row at the end
        const lastRow = updatedRows[updatedRows.length - 1];
        const hasEmptyRowAtEnd =
          lastRow && !lastRow.isSaved && isRowEmpty(lastRow);

        if (!hasEmptyRowAtEnd) {
          updatedRows.push(createEmptyRow());
        }

        return updatedRows;
      });

      // Clear all editing states after successful save
      setEditingRows(new Set());
      setShowEditMessage(false);

      // Show success message
      setSnackbar({
        open: true,
        message: "Course Planning Updated",
        severity: "success",
      });
    } catch (error) {
      console.error("Error saving course planning data:", error);
      setSnackbar({
        open: true,
        message: "Error saving data. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper: log cancel request if status is set to Course Cancel Requested
  const logCancelRequestIfNeeded = async (row, prevStatus) => {
    if (
      row.status === "Course Cancel Requested" &&
      prevStatus !== "Course Cancel Requested"
    ) {
      await logEvent(
        "Course Cancel Requested",
        `Course "${row.courseName}" cancellation has been requested by admin or school user.`,
        row,
        null,
        row.schoolId || schoolId,
      );
    }
  };

  // Patch handleSaveRow to log cancel request
  const handleSaveRow = async (rowToSave) => {
    try {
      setLoading(true);
      let savedDocId = null;
      const prevStatus = rowToSave.status;

      // Validate the specific row
      const errors = validateRow(rowToSave);

      if (errors.length > 0) {
        const actualRowIndex = rows.indexOf(rowToSave) + 1;
        setSnackbar({
          open: true,
          message: `Row ${actualRowIndex}: ${errors.join(", ")}`,
          severity: "error",
        });
        return;
      }

      if (rowToSave.isSaved) {
        // Update existing row
        if (rowToSave.id && typeof rowToSave.id === "string") {
          const docRef = doc(db, "CoursePlanningForm", rowToSave.id);
          const updateData = {
            courseName: rowToSave.courseName,
            numberOfSections: parseInt(rowToSave.numberOfSections),
            startDate: rowToSave.startDate
              ? rowToSave.startDate.toISOString()
              : null,
            endDate: rowToSave.endDate ? rowToSave.endDate.toISOString() : null,
            instructorType: rowToSave.instructorType,
            hsInstructorName: rowToSave.hsInstructorName,
            hsComments: rowToSave.hsComments,
            proctorName: rowToSave.proctorName,
            term: rowToSave.term,
            modality: rowToSave.modality,
            days: rowToSave.days,
            startTime: rowToSave.startTime,
            endTime: rowToSave.endTime,
            updatedAt: new Date().toISOString(),
            updatedBy: currentUser?.uid,
          };
          await updateDoc(docRef, updateData);

          // Log the update
          if (rowToSave.originalValues) {
            // Helper function to compare values (handles dayjs objects)
            const valuesAreEqual = (val1, val2) => {
              // Handle dayjs objects
              if (val1 && val1.$isDayjsObject && val2 && val2.$isDayjsObject) {
                return val1.isSame(val2);
              }
              // Handle case where one is dayjs and other is null/undefined
              if (
                (val1 && val1.$isDayjsObject) ||
                (val2 && val2.$isDayjsObject)
              ) {
                if (!val1 && !val2) return true;
                if (!val1 || !val2) return false;
                return val1.isSame && val1.isSame(val2);
              }
              // Handle regular values
              return val1 === val2;
            };

            // Create changed fields array with proper structure
            const changedFields = [];
            Object.keys(rowToSave.originalValues).forEach((field) => {
              if (
                !valuesAreEqual(
                  rowToSave.originalValues[field],
                  rowToSave[field],
                )
              ) {
                changedFields.push({
                  field,
                  oldValue: rowToSave.originalValues[field],
                  newValue: rowToSave[field],
                });
              }
            });

            if (changedFields.length > 0) {
              await logBulkUpdate(
                changedFields,
                rowToSave,
                rowToSave.originalValues,
              );
            }
          }
        }
      } else {
        // Save new row
        const formData = {
          courseName: rowToSave.courseName,
          numberOfSections: parseInt(rowToSave.numberOfSections),
          startDate: rowToSave.startDate
            ? rowToSave.startDate.toISOString()
            : null,
          endDate: rowToSave.endDate ? rowToSave.endDate.toISOString() : null,
          instructorType: rowToSave.instructorType,
          hsInstructorName: rowToSave.hsInstructorName,
          hsComments: rowToSave.hsComments,
          proctorName: rowToSave.proctorName,
          term: rowToSave.term,
          modality: rowToSave.modality,
          days: rowToSave.days,
          startTime: rowToSave.startTime,
          endTime: rowToSave.endTime,
          schoolId: schoolId,
          createdBy: currentUser?.uid,
          createdAt: new Date().toISOString(),
        };

        const newDocRef = await addDoc(
          collection(db, "CoursePlanningForm"),
          formData,
        );
        savedDocId = newDocRef.id;

        // Log the addition
        await logAddition(rowToSave);
      }

      // Update the row state
      setRows((prevRows) => {
        return prevRows.map((row) => {
          if (row.id === rowToSave.id) {
            const updatedRow = { ...row, hasChanges: false };
            if (!row.isSaved) {
              updatedRow.isSaved = true;
              if (savedDocId) {
                updatedRow.id = savedDocId; // Update with Firestore document ID
              }
            }
            updatedRow.originalValues = {
              courseName: row.courseName,
              numberOfSections: row.numberOfSections,
              startDate: row.startDate,
              endDate: row.endDate,
              instructorType: row.instructorType,
              hsInstructorName: row.hsInstructorName,
              hsComments: row.hsComments,
              proctorName: row.proctorName,
              term: row.term,
              modality: row.modality,
              days: row.days,
              startTime: row.startTime,
              endTime: row.endTime,
            };
            return updatedRow;
          }
          return row;
        });
      });

      // Log cancel request if needed
      await logCancelRequestIfNeeded(rowToSave, prevStatus);

      // Clear editing state for this row
      setEditingRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(rowToSave.id);
        return newSet;
      });

      // Hide edit message if no rows are being edited
      setEditingRows((current) => {
        if (current.size === 0) {
          setShowEditMessage(false);
        }
        return current;
      });

      // Show success message
      setSnackbar({
        open: true,
        message: "Row saved successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Error saving row:", error);
      setSnackbar({
        open: true,
        message: "Error saving row. Please try again.",
        severity: "error",
      });
    }
  };

  // Request course cancellation and log immediately
  const handleRequestCancelCourse = async (row) => {
    // Set status and save immediately to ensure log is created
    const updatedRow = { ...row, status: "Course Cancel Requested" };
    await handleSaveRow(updatedRow);
  };

  // Approve a change request
  const handleApproveChange = async (row) => {
    try {
      setLoading(true);
      const docRef = doc(db, "CoursePlanningForm", row.id);
      await updateDoc(docRef, {
        changeRequestStatus: "approved",
        approvedBy: currentUser?.uid || "admin",
        approvedAt: new Date().toISOString(),
        isPending: false,
      });
      setRows((prevRows) =>
        prevRows.map((r) =>
          r.id === row.id
            ? {
                ...r,
                changeRequestStatus: "approved",
                approvedBy: currentUser?.uid || "admin",
                approvedAt: new Date().toISOString(),
                isPending: false,
              }
            : r,
        ),
      );
      setSnackbar({
        open: true,
        message: "Change request approved.",
        severity: "success",
      });
    } catch (error) {
      console.error("Error approving change request:", error);
      setSnackbar({
        open: true,
        message: "Error approving change request.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Reject a change request
  const handleRejectChange = async (row) => {
    try {
      setLoading(true);
      const docRef = doc(db, "CoursePlanningForm", row.id);
      await updateDoc(docRef, {
        changeRequestStatus: "rejected",
        rejectedBy: currentUser?.uid || "admin",
        rejectedAt: new Date().toISOString(),
        isPending: false,
      });
      setRows((prevRows) =>
        prevRows.map((r) =>
          r.id === row.id
            ? {
                ...r,
                changeRequestStatus: "rejected",
                rejectedBy: currentUser?.uid || "admin",
                rejectedAt: new Date().toISOString(),
                isPending: false,
              }
            : r,
        ),
      );
      setSnackbar({
        open: true,
        message: "Change request rejected.",
        severity: "success",
      });
    } catch (error) {
      console.error("Error rejecting change request:", error);
      setSnackbar({
        open: true,
        message: "Error rejecting change request.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Handle field focus to show editing state
  const handleFieldFocus = (rowId) => {
    setEditingRows((prev) => new Set(prev).add(rowId));
    setShowEditMessage(true);
  };

  // Handle field blur - only remove from editing if no changes are pending
  const handleFieldBlur = (rowId) => {
    const row = rows.find((r) => r.id === rowId);
    // Don't remove editing state if there are unsaved changes
    if (row && !row.hasChanges && row.isSaved) {
      setEditingRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(rowId);
        return newSet;
      });
    }
  };

  // Add missing handlers to resolve ESLint errors
  const handleCompleteCancellation = async (row) => {
    try {
      setLoading(true);
      // Update the row's status to 'Course Canceled Complete'
      const updatedRow = { ...row, status: "Course Canceled Complete" };

      // Persist the change in Firestore if the row is saved
      if (row.id && row.isSaved) {
        const docRef = doc(db, "CoursePlanningForm", row.id);
        await updateDoc(docRef, { status: "Course Canceled Complete" });
      }

      // Log the cancellation complete event
      await logEvent(
        "Course Canceled Complete",
        `Course "${row.courseName}" cancellation has been completed by admin or school user.`,
        updatedRow,
        null,
        updatedRow.schoolId || schoolId,
      );

      // Update the cancel request log entry to complete
      await updateCancelLogToComplete(row.id);

      // Update the row in local state
      setRows((prevRows) =>
        prevRows.map((r) =>
          r.id === row.id ? { ...r, status: "Course Canceled Complete" } : r,
        ),
      );

      setSnackbar({
        open: true,
        message: `Course cancellation completed for ${row.courseName || "course"}`,
        severity: "success",
      });
    } catch (error) {
      console.error("Error completing course cancellation:", error);
      setSnackbar({
        open: true,
        message: "Error completing course cancellation.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper to update cancel request log entry to 'Cancel Complete'
  const updateCancelLogToComplete = async (coursePlanningFormId) => {
    try {
      // Find the log entry for this course cancel request
      const q = query(
        collection(db, "CoursePlanningLog"),
        where("coursePlanningFormId", "==", coursePlanningFormId),
        where("type", "==", "Cancel Request"),
        where("status", "==", "Course Cancel Requested"),
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const logDoc = snapshot.docs[0];
        await updateDoc(logDoc.ref, {
          type: "Cancel Complete",
          status: "Course Canceled Complete",
          canceledBy: currentUser?.uid || "admin",
          canceledAt: new Date().toISOString(),
        });
      }
    } catch (err) {
      console.error("Error updating cancel log to complete:", err);
    }
  };

  // Move these above their first usage
  const openCancelDialog = (row) => {
    setCancelDialog({ open: true, row });
  };

  const closeCancelDialog = () => {
    setCancelDialog({ open: false, row: null });
  };

  if (coursesLoading || loadingExisting || schoolsLoading || termsLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
        sx={{ p: 3 }}
      >
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          {coursesLoading
            ? "Loading courses..."
            : schoolsLoading
              ? "Loading school information..."
              : termsLoading
                ? "Loading terms..."
                : "Loading existing course planning records..."}
        </Typography>
      </Box>
    );
  }

  if (coursesError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Error loading courses: {coursesError}</Alert>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header with action buttons */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Admin Course Planning Form
            </Typography>

            {/* Edit notification message */}
            {showEditMessage && editingRows.size > 0 && (
              <Alert
                severity="info"
                sx={{
                  mb: 2,
                  backgroundColor: "#e3f2fd",
                  "& .MuiAlert-icon": {
                    color: "#1976d2",
                  },
                }}
              >
                You are editing {editingRows.size} row
                {editingRows.size > 1 ? "s" : ""}. Don't forget to save your
                changes using the save button in each row or "Save All Unsaved"
                above.
              </Alert>
            )}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={addRow}
              >
                Add Course
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={loading}
              >
                {loading ? <CircularProgress size={20} /> : "Save All Unsaved"}
              </Button>
              {onCancel && (
                <Button
                  variant="outlined"
                  onClick={onCancel}
                  disabled={loading}
                >
                  Cancel
                </Button>
              )}
              <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
                <Chip
                  label={`${rows.filter((row) => row.isSaved).length} saved`}
                  color="success"
                  size="small"
                />
                <Chip
                  label={`${
                    rows.filter((row) => !row.isSaved && !isRowEmpty(row))
                      .length
                  } pending`}
                  color="warning"
                  size="small"
                />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Course entries - Card-based layout */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {rows.map((row, index) => {
            const fieldVisibility = getFieldVisibility(
              row.instructorType,
              row.modality,
            );

            // Determine row status indicator
            let statusIndicator = "⚪"; // New/empty
            let statusColor = "#9e9e9e"; // Gray
            let statusText = "New";

            if (row.status === "Course Cancel Requested") {
              statusIndicator = "🔶"; // Cancel requested
              statusColor = "#008080"; // Teal
              statusText = "Course Cancel Requested";
            } else if (row.status === "Course Canceled Complete") {
              statusIndicator = "❌"; // Canceled
              statusColor = "#9c27b0"; // Purple
              statusText = "Course Canceled Complete";
            } else if (row.isSaved && !row.hasChanges) {
              statusIndicator = "🟢"; // Saved
              statusColor = "#4caf50"; // Green
              statusText = "Saved";
            } else if (editingRows.has(row.id) || row.hasChanges) {
              statusIndicator = "🔵"; // Editing
              statusColor = "#2196f3"; // Blue
              statusText = "Editing";
            }

            return (
              <Card
                key={row.id}
                sx={{
                  border: row.isPending
                    ? "2px solid #ff9800"
                    : editingRows.has(row.id) || row.hasChanges
                      ? "2px solid #2196f3"
                      : row.isSaved
                        ? "1px solid #4caf50"
                        : "1px solid #e0e0e0",
                  backgroundColor: row.isPending ? "#fff9e6" : "white",
                  position: "relative",
                }}
              >
                <CardContent sx={{ pb: 2 }}>
                  {/* Row header with status and actions */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                      pb: 1,
                      borderBottom: "1px solid #e0e0e0",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          color: statusColor,
                          fontWeight: "bold",
                          fontSize: "1rem",
                        }}
                      >
                        {statusIndicator} Course {index + 1}
                      </Typography>
                      <Chip
                        label={statusText}
                        size="small"
                        sx={{
                          backgroundColor: statusColor,
                          color: "white",
                          fontSize: "0.75rem",
                        }}
                      />
                      {row.isPending && (
                        <Chip
                          label="Change Request Pending"
                          size="small"
                          sx={{
                            backgroundColor: "#ff9800",
                            color: "white",
                            fontSize: "0.75rem",
                          }}
                        />
                      )}
                      {row.changeRequestStatus === "approved" && (
                        <Chip
                          label="Change Approved"
                          size="small"
                          sx={{
                            backgroundColor: "#4caf50",
                            color: "white",
                            fontSize: "0.75rem",
                          }}
                        />
                      )}
                      {row.changeRequestStatus === "rejected" && (
                        <Chip
                          label="Change Rejected"
                          size="small"
                          sx={{
                            backgroundColor: "#f44336",
                            color: "white",
                            fontSize: "0.75rem",
                          }}
                        />
                      )}
                      {row.status === "Course Cancel Requested" && (
                        <Chip
                          label="Course Cancel Requested"
                          size="small"
                          sx={{
                            backgroundColor: "#008080", // Teal
                            color: "white",
                            fontSize: "0.75rem",
                          }}
                        />
                      )}
                      {row.status === "Course Canceled Complete" && (
                        <Chip
                          label="Course Canceled Complete"
                          size="small"
                          sx={{
                            backgroundColor: "#9c27b0", // Purple
                            color: "white",
                            fontSize: "0.75rem",
                          }}
                        />
                      )}
                    </Box>

                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      {/* Individual Save Button */}
                      {(row.hasChanges ||
                        (!row.isSaved && !isRowEmpty(row))) && (
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={<SaveIcon />}
                          onClick={() => handleSaveRow(row)}
                          disabled={loading}
                          sx={{ minWidth: "auto" }}
                        >
                          Save
                        </Button>
                      )}

                      {/* Complete Cancellation Button for courses with cancel requests */}
                      {row.status === "Course Cancel Requested" && (
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          onClick={() => handleCompleteCancellation(row)}
                          disabled={loading}
                          sx={{
                            minWidth: "auto",
                            backgroundColor: "#008080", // Teal
                            "&:hover": { backgroundColor: "#006666" },
                          }}
                        >
                          Complete Cancellation
                        </Button>
                      )}

                      {/* Delete Button */}
                      <IconButton
                        size="small"
                        onClick={() => deleteRow(row.id)}
                        disabled={loading}
                        color="error"
                        title="Delete this course"
                        sx={{
                          border: "1px solid #f44336",
                          "&:hover": { backgroundColor: "#ffebee" },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>

                      {/* Request Cancel Button - Only if not already requested/canceled */}
                      {row.status !== "Course Cancel Requested" &&
                        row.status !== "Course Canceled Complete" && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() => openCancelDialog(row)}
                            disabled={loading}
                            sx={{ minWidth: "auto" }}
                          >
                            Request Cancel
                          </Button>
                        )}
                    </Box>
                  </Box>

                  {/* Course Information Grid */}
                  <Grid container spacing={3}>
                    {/* Basic Information */}
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: "bold",
                          color: "#1976d2",
                          mb: 1,
                          borderBottom: "1px solid #e3f2fd",
                          pb: 0.5,
                        }}
                      >
                        Basic Information
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      {fieldVisibility.courseName && (
                        <SearchableCourseSelect
                          label="Course Name"
                          courses={courses}
                          value={row.courseName}
                          onChange={(value) =>
                            handleRowChange(row.id, "courseName", value)
                          }
                          onFocus={() => handleFieldFocus(row.id)}
                          onBlur={() => handleFieldBlur(row.id)}
                          size="small"
                          fullWidth
                          error={!row.courseName && !isRowEmpty(row)}
                          helperText={
                            !row.courseName && !isRowEmpty(row)
                              ? "Course name is required"
                              : ""
                          }
                          placeholder="Select or type course name"
                        />
                      )}
                    </Grid>

                    <Grid item xs={12} md={3}>
                      {fieldVisibility.numberOfSections && (
                        <TextField
                          label="Number of Sections"
                          size="small"
                          type="number"
                          value={row.numberOfSections}
                          onChange={(e) =>
                            handleRowChange(
                              row.id,
                              "numberOfSections",
                              e.target.value,
                            )
                          }
                          onFocus={() => handleFieldFocus(row.id)}
                          onBlur={() => handleFieldBlur(row.id)}
                          inputProps={{ min: 1 }}
                          fullWidth
                        />
                      )}
                    </Grid>

                    <Grid item xs={12} md={3}>
                      {fieldVisibility.instructorType && (
                        <TextField
                          label="Instructor Type"
                          size="small"
                          select
                          value={row.instructorType}
                          onChange={(e) =>
                            handleRowChange(
                              row.id,
                              "instructorType",
                              e.target.value,
                            )
                          }
                          onFocus={() => handleFieldFocus(row.id)}
                          onBlur={() => handleFieldBlur(row.id)}
                          fullWidth
                        >
                          <MenuItem value="">
                            <em>Select Type</em>
                          </MenuItem>
                          {instructorTypeOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    </Grid>

                    {/* Schedule Information - Only show if instructor type is selected */}
                    {row.instructorType && (
                      <>
                        <Grid item xs={12} sx={{ mt: 2 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: "bold",
                              color: "#1976d2",
                              mb: 1,
                              borderBottom: "1px solid #e3f2fd",
                              pb: 0.5,
                            }}
                          >
                            Schedule Information
                          </Typography>
                        </Grid>

                        <Grid item xs={12} md={4}>
                          {fieldVisibility.term && (
                            <TextField
                              label="Term"
                              size="small"
                              select
                              value={row.term}
                              onChange={(e) =>
                                handleRowChange(row.id, "term", e.target.value)
                              }
                              onFocus={() => handleFieldFocus(row.id)}
                              onBlur={() => handleFieldBlur(row.id)}
                              fullWidth
                              helperText={
                                row.term && !termOptions.includes(row.term)
                                  ? `Warning: "${row.term}" not found in available options`
                                  : null
                              }
                              error={
                                row.term && !termOptions.includes(row.term)
                              }
                            >
                              <MenuItem value="">
                                <em>Select Term</em>
                              </MenuItem>
                              {termOptions.map((option) => (
                                <MenuItem key={option} value={option}>
                                  {option}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                        </Grid>

                        <Grid item xs={12} md={4}>
                          {fieldVisibility.startDate && (
                            <DatePicker
                              label="Start Date"
                              value={row.startDate}
                              onChange={(newValue) =>
                                handleRowChange(row.id, "startDate", newValue)
                              }
                              onOpen={() => handleFieldFocus(row.id)}
                              onClose={() => handleFieldBlur(row.id)}
                              disabled={getSelectedTermData(row.term)}
                              slotProps={{
                                textField: {
                                  size: "small",
                                  fullWidth: true,
                                },
                              }}
                            />
                          )}
                        </Grid>

                        <Grid item xs={12} md={4}>
                          {fieldVisibility.endDate && (
                            <DatePicker
                              label="End Date"
                              value={row.endDate}
                              onChange={(newValue) =>
                                handleRowChange(row.id, "endDate", newValue)
                              }
                              onOpen={() => handleFieldFocus(row.id)}
                              onClose={() => handleFieldBlur(row.id)}
                              disabled={getSelectedTermData(row.term)}
                              slotProps={{
                                textField: {
                                  size: "small",
                                  fullWidth: true,
                                },
                              }}
                            />
                          )}
                        </Grid>

                        {/* Modality and Time Information - Only for Courses W Sinclair */}
                        {row.instructorType === "Courses W Sinclair" && (
                          <>
                            <Grid item xs={12} md={4}>
                              {fieldVisibility.modality && (
                                <TextField
                                  label="Modality"
                                  size="small"
                                  select
                                  value={row.modality}
                                  onChange={(e) =>
                                    handleRowChange(
                                      row.id,
                                      "modality",
                                      e.target.value,
                                    )
                                  }
                                  onFocus={() => handleFieldFocus(row.id)}
                                  onBlur={() => handleFieldBlur(row.id)}
                                  fullWidth
                                >
                                  <MenuItem value="">
                                    <em>Select Modality</em>
                                  </MenuItem>
                                  {modalityOptions.map((option) => (
                                    <MenuItem key={option} value={option}>
                                      {option}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              )}
                            </Grid>

                            {row.modality === "Face 2 Face" && (
                              <>
                                <Grid item xs={12} md={4}>
                                  {fieldVisibility.days && (
                                    <TextField
                                      label="Days"
                                      size="small"
                                      value={row.days}
                                      onChange={(e) =>
                                        handleRowChange(
                                          row.id,
                                          "days",
                                          e.target.value,
                                        )
                                      }
                                      onFocus={() => handleFieldFocus(row.id)}
                                      onBlur={() => handleFieldBlur(row.id)}
                                      fullWidth
                                      placeholder="M,T,W,Th,F"
                                    />
                                  )}
                                </Grid>

                                <Grid item xs={6} md={2}>
                                  {fieldVisibility.startTime && (
                                    <TextField
                                      label="Start Time"
                                      size="small"
                                      type="time"
                                      value={row.startTime}
                                      onChange={(e) =>
                                        handleRowChange(
                                          row.id,
                                          "startTime",
                                          e.target.value,
                                        )
                                      }
                                      onFocus={() => handleFieldFocus(row.id)}
                                      onBlur={() => handleFieldBlur(row.id)}
                                      InputLabelProps={{ shrink: true }}
                                      fullWidth
                                    />
                                  )}
                                </Grid>

                                <Grid item xs={6} md={2}>
                                  {fieldVisibility.endTime && (
                                    <TextField
                                      label="End Time"
                                      size="small"
                                      type="time"
                                      value={row.endTime}
                                      onChange={(e) =>
                                        handleRowChange(
                                          row.id,
                                          "endTime",
                                          e.target.value,
                                        )
                                      }
                                      onFocus={() => handleFieldFocus(row.id)}
                                      onBlur={() => handleFieldBlur(row.id)}
                                      InputLabelProps={{ shrink: true }}
                                      fullWidth
                                    />
                                  )}
                                </Grid>

                                <Grid item xs={12} md={4}>
                                  {fieldVisibility.proctorName && (
                                    <TextField
                                      label="Proctor Name"
                                      size="small"
                                      value={row.proctorName}
                                      onChange={(e) =>
                                        handleRowChange(
                                          row.id,
                                          "proctorName",
                                          e.target.value,
                                        )
                                      }
                                      onFocus={() => handleFieldFocus(row.id)}
                                      onBlur={() => handleFieldBlur(row.id)}
                                      fullWidth
                                    />
                                  )}
                                </Grid>
                              </>
                            )}

                            {row.modality !== "Face 2 Face" && (
                              <Grid item xs={12} md={4}>
                                {fieldVisibility.proctorName && (
                                  <TextField
                                    label="Proctor Name"
                                    size="small"
                                    value={row.proctorName}
                                    onChange={(e) =>
                                      handleRowChange(
                                        row.id,
                                        "proctorName",
                                        e.target.value,
                                      )
                                    }
                                    onFocus={() => handleFieldFocus(row.id)}
                                    onBlur={() => handleFieldBlur(row.id)}
                                    fullWidth
                                  />
                                )}
                              </Grid>
                            )}
                          </>
                        )}

                        {/* HS Faculty specific fields */}
                        {row.instructorType === "HS Faculty" && (
                          <Grid item xs={12} md={4}>
                            {fieldVisibility.hsInstructorName && (
                              <TextField
                                label="Instructor Name"
                                size="small"
                                value={row.hsInstructorName}
                                onChange={(e) =>
                                  handleRowChange(
                                    row.id,
                                    "hsInstructorName",
                                    e.target.value,
                                  )
                                }
                                onFocus={() => handleFieldFocus(row.id)}
                                onBlur={() => handleFieldBlur(row.id)}
                                fullWidth
                              />
                            )}
                          </Grid>
                        )}

                        {/* Comments - Show for both types */}
                        <Grid item xs={12}>
                          {fieldVisibility.hsComments && (
                            <TextField
                              label="Comments"
                              size="small"
                              multiline
                              rows={2}
                              value={row.hsComments}
                              onChange={(e) =>
                                handleRowChange(
                                  row.id,
                                  "hsComments",
                                  e.target.value,
                                )
                              }
                              onFocus={() => handleFieldFocus(row.id)}
                              onBlur={() => handleFieldBlur(row.id)}
                              fullWidth
                              placeholder="Additional comments or notes"
                            />
                          )}
                        </Grid>
                      </>
                    )}

                    {/* Change Request Information - Show if there are change requests */}
                    {row.isPending && row.requestedValues && (
                      <>
                        <Grid item xs={12} sx={{ mt: 2 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: "bold",
                              color: "#ff9800",
                              mb: 1,
                              borderBottom: "1px solid #fff3cd",
                              pb: 0.5,
                            }}
                          >
                            Pending Change Request - Review Changes
                          </Typography>
                        </Grid>

                        {/* Show requested changes in a comparison format */}
                        {Object.keys(row.requestedValues).map((fieldName) => {
                          const currentValue = row[fieldName];
                          const requestedValue = row.requestedValues[fieldName];

                          // Format values for display
                          const formatValue = (value, field) => {
                            if (!value) return "Not set";
                            if (field === "startDate" || field === "endDate") {
                              return formatDate(value);
                            }
                            return value;
                          };

                          const getFieldLabel = (field) => {
                            const labels = {
                              courseName: "Course Name",
                              numberOfSections: "Number of Sections",
                              startDate: "Start Date",
                              endDate: "End Date",
                              instructorType: "Instructor Type",
                              hsInstructorName: "Instructor Name",
                              hsComments: "Comments",
                              proctorName: "Proctor Name",
                              term: "Term",
                              modality: "Modality",
                              days: "Days",
                              startTime: "Start Time",
                              endTime: "End Time",
                            };
                            return labels[field] || field;
                          };

                          // Only show fields that actually changed
                          if (
                            formatValue(currentValue, fieldName) ===
                            formatValue(requestedValue, fieldName)
                          ) {
                            return null;
                          }

                          return (
                            <Grid item xs={12} md={6} key={fieldName}>
                              <Box
                                sx={{
                                  p: 2,
                                  border: "1px solid #fff3cd",
                                  borderRadius: 1,
                                  backgroundColor: "#fefefe",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: "bold", mb: 1 }}
                                >
                                  {getFieldLabel(fieldName)}
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                  }}
                                >
                                  <Box sx={{ flex: 1 }}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Current:
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        p: 1,
                                        backgroundColor: "#f5f5f5",
                                        borderRadius: 0.5,
                                        textDecoration: "line-through",
                                        color: "text.secondary",
                                      }}
                                    >
                                      {formatValue(currentValue, fieldName)}
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="h6"
                                    sx={{ color: "#ff9800" }}
                                  >
                                    →
                                  </Typography>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Requested:
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        p: 1,
                                        backgroundColor: "#e8f5e8",
                                        borderRadius: 0.5,
                                        fontWeight: "bold",
                                        color: "#2e7d32",
                                      }}
                                    >
                                      {formatValue(requestedValue, fieldName)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            </Grid>
                          );
                        })}

                        <Grid item xs={12} md={4}>
                          <TextField
                            label="Change Action"
                            size="small"
                            value={row.changeAction}
                            fullWidth
                            disabled={true}
                            InputProps={{
                              style: {
                                backgroundColor: "#fff3cd",
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Change Details"
                            size="small"
                            multiline
                            rows={1}
                            value={row.specificChange}
                            fullWidth
                            disabled={true}
                            InputProps={{
                              style: {
                                backgroundColor: "#fff3cd",
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={2}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          >
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<CheckIcon />}
                              onClick={() => handleApproveChange(row)}
                              fullWidth
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => handleRejectChange(row)}
                              fullWidth
                            >
                              Reject
                            </Button>
                          </Box>
                        </Grid>
                      </>
                    )}

                    {/* Legacy Change Request Information - Show for old-style change requests without requested values */}
                    {(row.changeAction || row.specificChange) &&
                      !row.isPending && (
                        <>
                          <Grid item xs={12} sx={{ mt: 2 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: "bold",
                                color: "#4caf50",
                                mb: 1,
                                borderBottom: "1px solid #d4edda",
                                pb: 0.5,
                              }}
                            >
                              Change Request History
                            </Typography>
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <TextField
                              label="Change Action"
                              size="small"
                              value={row.changeAction}
                              fullWidth
                              disabled={true}
                              InputProps={{
                                style: {
                                  backgroundColor: "#d4edda",
                                },
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <TextField
                              label="Change Details"
                              size="small"
                              multiline
                              rows={1}
                              value={row.specificChange}
                              fullWidth
                              disabled={true}
                              InputProps={{
                                style: {
                                  backgroundColor: "#d4edda",
                                },
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} md={2}>
                            <Chip
                              label={
                                row.changeRequestStatus === "approved"
                                  ? "Approved"
                                  : "Rejected"
                              }
                              color={
                                row.changeRequestStatus === "approved"
                                  ? "success"
                                  : "error"
                              }
                              size="small"
                            />
                          </Grid>
                        </>
                      )}
                  </Grid>
                </CardContent>
              </Card>
            );
          })}
        </Box>

        {/* Dialogs and Snackbar */}
        <ConfirmDeleteDialog
          open={deleteDialog.open}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Confirm Deletion"
          message={
            <>
              Are you sure you want to delete this course entry?
              {deleteDialog.rowToDelete && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: "#f5f5f5",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    Course:{" "}
                    {deleteDialog.rowToDelete.courseName || "Not specified"}
                  </Typography>
                  <Typography variant="body2">
                    Sections:{" "}
                    {deleteDialog.rowToDelete.numberOfSections ||
                      "Not specified"}
                  </Typography>
                  <Typography variant="body2">
                    Instructor Type:{" "}
                    {deleteDialog.rowToDelete.instructorType || "Not specified"}
                  </Typography>
                </Box>
              )}
            </>
          }
        />

        <Dialog
          open={cancelDialog.open}
          onClose={closeCancelDialog}
          aria-labelledby="cancel-confirmation-dialog-title"
          aria-describedby="cancel-confirmation-dialog-description"
        >
          <ModalDialogTitle
            onClose={closeCancelDialog}
            id="cancel-confirmation-dialog-title"
          >
            Confirm Course Cancellation
          </ModalDialogTitle>
          <DialogContent>
            <DialogContentText id="cancel-confirmation-dialog-description">
              Are you sure you want to request cancellation for this course?
              <br />
              This action will immediately log a "Course Cancel Requested" entry
              for admin review.
              {cancelDialog.row && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: "#f5f5f5",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    Course: {cancelDialog.row.courseName || "Not specified"}
                  </Typography>
                  <Typography variant="body2">
                    Sections:{" "}
                    {cancelDialog.row.numberOfSections || "Not specified"}
                  </Typography>
                  <Typography variant="body2">
                    Instructor Type:{" "}
                    {cancelDialog.row.instructorType || "Not specified"}
                  </Typography>
                </Box>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeCancelDialog} color="primary">
              No, keep course
            </Button>
            <Button
              onClick={() => {
                handleRequestCancelCourse(cancelDialog.row);
                closeCancelDialog();
              }}
              color="error"
              variant="contained"
              autoFocus
            >
              Yes, request cancellation
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default AdminCoursePlanningForm;
